import { useChannelTalk } from '@repo/channel-talk';
import { Description } from '@zenterprise-inc/ui';
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { TrpContentQuery } from '@/__generated__/TrpContentQuery.graphql';
import LoaderAnimation from '@/assets/img/loader-animation.svg';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';

interface Props {
  token: string;
}
type TrpContentProps = Props;

const trpContentQuery = graphql`
  query TrpContentQuery($talkToken: String!) {
    paymentCardLink(talkToken: $talkToken) {
      result
      paymentUrl
      errors {
        code
      }
    }
  }
`;

const TrpContent: FC<TrpContentProps> = ({ token }) => {
  const router = useRouter();
  const { openChannelTalk } = useChannelTalk();
  const data = useLazyLoadQuery<TrpContentQuery>(
    trpContentQuery,
    {
      talkToken: token
    },
    {
      fetchPolicy: 'network-only'
    }
  );

  const content = useMemo(() => {
    if (data?.paymentCardLink?.errors) {
      switch (data?.paymentCardLink?.errors?.code) {
        case 'ERR_EXPIRE_URL':
          return (
            <Description.Guide
              subTitle={'예상하지 못한 문제가 발생했어요'}
              description={'이용에 불편을 드려 죄송합니다.\n문제가 반복되면 고객센터로 문의해주세요.'}
              imgChild={
                <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/common/iconography/icg_exclamation-mark-dimmed_sm.svg`} className={'w-20'} alt={''} />
              }
              button={{
                state: 'basic',
                onClick: () => {
                  openChannelTalk(SUPPORT_BOT_LINK.errors);
                }
              }}
              buttonContent={'문의하기'}
            />
          );

        case 'ERR_ETC':
        default:
          return (
            <Description.Guide
              subTitle="잘못된 접근입니다"
              description="페이지에 접근할 수 없습니다."
              imgChild={
                <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/common/iconography/icg_exclamation-mark-dimmed_sm.svg`} className={'w-20'} alt={''} />
              }
            />
          );
      }
    }

    return <Description.Guide title="카드 결제를 위해 이동합니다" titleSize="medium" imgChild={<LoaderAnimation width={80} height={80} />} />;
  }, [data]);

  useEffect(() => {
    if (data?.paymentCardLink?.paymentUrl) {
      const url = data?.paymentCardLink?.paymentUrl;
      router.push(url);
    }
  }, [data]);

  return <div className="flex h-[calc(100vh-100px)] grow items-center">{content}</div>;
};

export default TrpContent;
